import { cloneDeep } from 'lodash';

import { PcodeCanvasItem, PcodePreviewOptions, PcodeRenderDataCanvas } from '../../../typings/pcode';
import {
    Bundle,
    CropApiImageItem,
    FetchCommonFileDataResponse,
    ImageApiGalleryItem
} from '../../common/requestsTypings';
import { CreativeImageData, CreativeType, ResourceType } from '../../common/typings';
import {
    CreativeImageCropSize,
    CreativeImageCropSizeData,
    CreativeImageCropSizesValue
} from '../../CreativeImageCropModal/typings';
import {
    CommonOptionGroupName,
    CommonOptionName,
    CreativeOptionsData,
    CreativeOptionsGroupValue,
    CreativeOptionType
} from '../../CreativeOptions/typings';
import { CreativeSize } from '../../CreativeSizesList/typings';
import { GalleryCommonImageCollectionItem } from '../../GalleryPane/typings';
import { getCroppedMediaSetItem, getMediaSet } from '../helpers';
import {
    AddCommonCreativeState,
    AddCreativeState,
    CommonCreativeBaseItem,
    CommonCreativeItem,
    CommonCreativePreviewItem
} from '../typings';

function getPcodePreviewOptions(value?: CreativeOptionsGroupValue, isAdaptive?: boolean): PcodePreviewOptions {
    if (!value) {
        return {
            isAdaptive
        };
    }

    const minWidth = value.options[CommonOptionName.minWidth]
        ? Number(value.options[CommonOptionName.minWidth])
        : undefined;
    const minHeight = value.options[CommonOptionName.minHeight]
        ? Number(value.options[CommonOptionName.minHeight])
        : undefined;

    return {
        backgroundColor: String(value.options[CommonOptionName.backgroundColor]),
        borderColor: String(value.options[CommonOptionName.borderColor]),
        hasAnimation: true,
        isAdaptive,
        minHeight,
        minWidth
    };
}

function getPcodeCanvasItem(item: CreativeOptionsGroupValue): PcodeCanvasItem {
    const available = Boolean(item.visible);

    switch (item.type) {
        case CommonOptionGroupName.image: {
            return {
                type: 'image',
                available,
                mediaSet: String(item.mediaSet),
                options: {
                    width: item.options[CommonOptionName.width],
                    height: item.options[CommonOptionName.height]
                }
            };
        }

        case CommonOptionGroupName.logo: {
            return {
                type: 'logo',
                available,
                mediaSet: String(item.mediaSet),
                options: {
                    width: item.options[CommonOptionName.width],
                    height: item.options[CommonOptionName.height]
                }
            };
        }

        case CommonOptionGroupName.headline: {
            return {
                type: 'headline',
                available,
                maxLength: Number(item.maxLength),
                options: {
                    placeholder: item.options[CommonOptionName.placeholder] || '',
                    content: item.options[CommonOptionName.content] || item.options[CommonOptionName.placeholder] || '',
                    color: item.options[CommonOptionName.color] || ''
                }
            };
        }

        case CommonOptionGroupName.button: {
            return {
                type: 'button',
                available,
                options: {
                    placeholder: item.options[CommonOptionName.placeholder] || '',
                    content: item.options[CommonOptionName.content] || item.options[CommonOptionName.placeholder] || '',
                    color: item.options[CommonOptionName.color] || '',
                    backgroundColor: item.options[CommonOptionName.backgroundColor] || ''
                }
            };
        }

        case CommonOptionGroupName.fade: {
            return {
                type: 'fade',
                available,
                options: {
                    color: item.options[CommonOptionName.color] || ''
                }
            };
        }

        case CommonOptionGroupName.description: {
            return {
                type: 'description',
                available,
                maxLength: Number(item.maxLength),
                options: {
                    placeholder: item.options[CommonOptionName.placeholder] || '',
                    content: item.options[CommonOptionName.content] || item.options[CommonOptionName.placeholder] || '',
                    color: item.options[CommonOptionName.color] || ''
                }
            };
        }

        case CommonOptionGroupName.domain: {
            const color = item.options[CommonOptionName.color] || '';

            return {
                type: 'domain',
                available,
                options: {
                    placeholder: item.options[CommonOptionName.placeholder] || '',
                    content: item.options[CommonOptionName.content] || item.options[CommonOptionName.placeholder] || '',
                    ...(color ? { color } : {})
                }
            };
        }

        case CommonOptionGroupName.disclaimer: {
            const color = item.options[CommonOptionName.color] || '';

            return {
                type: 'disclaimer',
                available,
                options: {
                    countryId: item.options[CommonOptionName.disclaimerCountry] || '',
                    disclaimerId: item.options[CommonOptionName.disclaimer] || '',
                    content: item.options[CommonOptionName.content] || '',
                    ...(color ? { color } : {})
                }
            };
        }

        case CommonOptionGroupName.special: {
            return {
                type: 'special',
                available,
                options: {
                    placeholder: item.options[CommonOptionName.placeholder] || '',
                    color: item.options[CommonOptionName.color] || '',
                    backgroundColor: item.options[CommonOptionName.backgroundColor] || '',
                    content: item.options[CommonOptionName.content] || item.options[CommonOptionName.placeholder] || ''
                }
            };
        }

        case CommonOptionGroupName.legal: {
            return {
                type: 'legal',
                available,
                options: {
                    placeholder: item.options[CommonOptionName.placeholder] || '',
                    backgroundColor: item.options[CommonOptionName.backgroundColor] || '',
                    iconColor: item.options[CommonOptionName.iconColor] || '',
                    color: item.options[CommonOptionName.color] || '',
                    content: item.options[CommonOptionName.content] || item.options[CommonOptionName.placeholder] || ''
                }
            };
        }

        case CommonOptionGroupName.ageRestriction: {
            return {
                type: 'ageRestriction',
                available,
                options: {
                    value: item.options[CommonOptionName.value] || '',
                    color: item.options[CommonOptionName.color] || '',
                    content: item.options[CommonOptionName.content] || ''
                }
            };
        }

        default:
            throw 'Unsupported option type!';
    }
}

function getPcodeElements(elements: CreativeOptionsGroupValue[]): PcodeCanvasItem[] {
    return elements
        .filter(item => {
            // те, которые не видно, отдавать на бек не надо
            // и если это логотип параметр без mediaSet, то отдавать его также не надо
            return item.visible && (item.type !== CommonOptionGroupName.logo || Boolean(item.mediaSet));
        })
        .map((item: CreativeOptionsGroupValue) => getPcodeCanvasItem(item));
}

export const getCommonPcodeRenderData = (
    item: CommonCreativeItem,
    options: CreativeOptionsData,
    bundle: Bundle,
    isAdaptive?: boolean
): PcodeRenderDataCanvas => {
    const commonOptions = options.optionsValue.find(el => el.type === 'common');
    const elements = options.optionsValue.filter(el => el.type !== 'common');

    return {
        width: item.data.width,
        height: item.data.height,
        mediaSets: cloneDeep(item.mediaSets),
        options: getPcodePreviewOptions(commonOptions, isAdaptive),
        bundle,
        elements: getPcodeElements(elements)
    };
};

export function getModalPreviewItems(
    items: CommonCreativeItem[],
    options: CreativeOptionsData,
    bundle: Bundle,
    withCropParameteres?: boolean
): CommonCreativePreviewItem[] {
    return items.map(item => ({
        id: item.id,
        selected: item.selected,
        data: item.data,
        pcodeData: getCommonPcodeRenderData(item, options, bundle),
        cropParameters: withCropParameteres ? item.cropParameters : undefined,
        cropSize: item.cropSize
    }));
}

export function getModalPreviewItemsAfterCrop(
    state: AddCommonCreativeState,
    croppedFiles: CropApiImageItem[],
    editedSizes: CreativeImageCropSize[]
): CommonCreativePreviewItem[] | undefined {
    const { items, creativeOptions, bundle, editingFileInfo } = state;

    if (!items || !creativeOptions || !bundle || !editingFileInfo) {
        return undefined;
    }

    const previewItems = state.previewItems
        ? state.previewItems
        : getModalPreviewItems(items, creativeOptions, bundle, true);
    const mediaSet = editingFileInfo.mediaSetId;

    editedSizes.forEach((size, index) => {
        const item = previewItems.find(i => i.id === size.id);
        if (!item || !item.pcodeData) {
            return;
        }

        item.cropParameters = croppedFiles[index].cropParameters;
        item.pcodeData.mediaSets[mediaSet].items[0].items[0] = getCroppedMediaSetItem(
            croppedFiles[index],
            getMediaSet(item.pcodeData.mediaSets, mediaSet).alias,
            editingFileInfo.fileId
        );
    });

    return previewItems;
}

function getCropSizes(items: (CommonCreativeItem | CommonCreativePreviewItem)[]): CreativeImageCropSize[] {
    return items.map(item => ({
        id: item.id,
        ...item.cropSize
    }));
}

function getSizes(items: CommonCreativeBaseItem[]): CreativeSize[] {
    return items.map(item => ({
        id: item.id,
        height: item.data.height,
        width: item.data.width,
        available: item.selected
    }));
}

export function getCreativeImageData(item: ImageApiGalleryItem | GalleryCommonImageCollectionItem): CreativeImageData {
    return {
        src: item.cropSourceUrl,
        resourceId: item.id
    };
}

export function getCreativeSizeData(
    currentId: number,
    items: (CommonCreativeItem | CommonCreativePreviewItem)[]
): CreativeImageCropSizeData {
    return {
        selectedSize: currentId,
        cropSizes: getCropSizes(items),
        sizes: getSizes(items)
    };
}

export function isOptionCroppable(groupName: string, options: CreativeOptionsData): boolean {
    const groupConfig = options.optionsConfig[groupName].options;
    const option = groupConfig.find(
        el => el.type === CreativeOptionType.File && el.resourceType === ResourceType.image
    );

    if (!option || option.type !== CreativeOptionType.File || option.resourceType !== ResourceType.image) {
        return false;
    }

    return option.isCroppable;
}

export const getCropSizesValue = (items: CommonCreativeBaseItem[]): CreativeImageCropSizesValue => {
    return items.reduce((value: CreativeImageCropSizesValue, item: CommonCreativeBaseItem) => {
        if (!item.cropParameters) {
            return value;
        }

        value[item.id] = {
            crop: item.cropParameters
        };

        return value;
    }, {});
};

export const getCroppedFileIds = (
    items: CommonCreativeItem[]
): {
    croppedFileId: string;
    itemId: number;
}[] =>
    items.reduce(
        (result, item) => {
            let croppedFileId;

            Object.keys(item.mediaSets).forEach(mediaSet => {
                const mediaSetItem = getMediaSet(item.mediaSets, mediaSet);
                if (mediaSetItem.croppedFileId) {
                    croppedFileId = mediaSetItem.croppedFileId;
                }
            });

            if (croppedFileId) {
                result.push({
                    croppedFileId,
                    itemId: item.id
                });
            }

            return result;
        },
        [] as {
            croppedFileId: string;
            itemId: number;
        }[]
    );

export const getCommonItemsWithCropParameteres = (
    items: CommonCreativeItem[],
    croppedFiles: {
        croppedFileId: string;
        itemId: number;
    }[],
    apiFiles: FetchCommonFileDataResponse[]
): CommonCreativeItem[] => {
    const result = items.slice();

    croppedFiles.forEach((file, index) => {
        const item = items.find(i => i.id === file.itemId);

        if (item) {
            item.cropParameters = apiFiles[index].cropParameters;
        }
    });

    return result;
};

export function isCommonOptionsAvailable(type?: CreativeType) {
    switch (type) {
        case CreativeType.CPM_GEOPRODUCT:
            return false;

        default:
            return true;
    }
}

export const getCommonCreativeState = (state: AddCreativeState): AddCommonCreativeState | undefined => {
    switch (state.type) {
        case CreativeType.COMMON:
        case CreativeType.CPM_GEOPRODUCT:
            return state;

        default:
            return undefined;
    }
};
